import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogWithScrollReset as Dialog } from "@/components/ui/dialog-with-scroll-reset";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Link, User, Users, Building2, CheckCircle2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { Employee, Team, License, Application } from "@shared/schema";

type AssignLicenseDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "employee" | "team";
  entityId: number;
};

export default function AssignLicenseDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
}: AssignLicenseDialogProps) {
  const [selectedLicenseId, setSelectedLicenseId] = useState<string>("");
  const [isAssigning, setIsAssigning] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch entity data (employee or team)
  const { 
    data: entity,
    isLoading: isLoadingEntity,
  } = useQuery({
    queryKey: entityType === "employee" 
      ? ['/api/employees', entityId] 
      : ['/api/teams', entityId],
    retry: 1,
  });

  // Fetch available licenses
  const { 
    data: licenses = [] as License[], 
    isLoading: isLoadingLicenses 
  } = useQuery<License[]>({
    queryKey: ['/api/licenses'],
    retry: 1,
  });

  // Fetch applications for display purposes
  const { 
    data: applications = [] as Application[],
    isLoading: isLoadingApplications,
  } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
    retry: 1,
  });

  // Assign license mutation
  const assignLicenseMutation = useMutation({
    mutationFn: (licenseId: number) => {
      const endpoint = `/api/licenses/${licenseId}/${entityType === "employee" ? "assign-employee" : "assign-team"}`;
      const body = entityType === "employee" 
        ? { employeeId: entityId }
        : { teamId: entityId };
      
      console.log(`Assigning license ${licenseId} to ${entityType} ${entityId}`);
        
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }).then(res => {
        if (!res.ok) {
          return res.json().then(data => {
            throw new Error(data.message || 'Failed to assign license');
          });
        }
        return res.json();
      });
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      
      if (entityType === "employee") {
        queryClient.invalidateQueries({ queryKey: ['/api/employees', entityId] });
        queryClient.invalidateQueries({ queryKey: ['/api/employees'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['/api/teams', entityId] });
        queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      }
      
      toast({
        title: "License assigned",
        description: `License has been successfully assigned to the ${entityType}.`,
      });
      
      setIsAssigning(false);
      setTimeout(() => {
        onOpenChange(false);
      }, 1000);
    },
    onError: (error) => {
      toast({
        title: "Assignment failed",
        description: "There was an error assigning the license. Please try again.",
        variant: "destructive",
      });
      setIsAssigning(false);
    },
  });

  // Handle assignment
  const handleAssign = () => {
    if (!selectedLicenseId) {
      toast({
        title: "No license selected",
        description: "Please select a license to assign.",
        variant: "destructive",
      });
      return;
    }
    
    setIsAssigning(true);
    assignLicenseMutation.mutate(parseInt(selectedLicenseId, 10));
  };

  // Find application name for a license
  const getApplicationName = (applicationId: number) => {
    const app = (applications as Application[]).find(app => app.id === applicationId);
    return app ? app.name : "Unknown Application";
  };

  // Get entity name based on type
  const getEntityName = () => {
    if (!entity) return entityType === "employee" ? "Employee" : "Team";
    
    // Type guard for employee vs team
    if (entityType === "employee" && entity && typeof entity === 'object' && "fullName" in entity) {
      return entity.fullName as string;
    } else if (entityType === "team" && entity && typeof entity === 'object' && "name" in entity) {
      return entity.name as string;
    }
    
    return entityType === "employee" ? "Employee" : "Team";
  };

  const isLoading = isLoadingEntity || isLoadingLicenses || isLoadingApplications;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Link className="h-5 w-5 mr-2 text-primary" />
            Assign License to {entityType === "employee" ? "Employee" : "Team"}
          </DialogTitle>
          <DialogDescription>
            {isLoadingEntity ? "Loading..." : `Assign a license to ${getEntityName()}`}
          </DialogDescription>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <Card className="bg-slate-50/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  {entityType === "employee" ? (
                    <User className="h-10 w-10 text-primary p-2 bg-primary/10 rounded-full" />
                  ) : (
                    <Building2 className="h-10 w-10 text-primary p-2 bg-primary/10 rounded-full" />
                  )}
                  <div>
                    <h3 className="font-medium">{getEntityName()}</h3>
                    <p className="text-sm text-slate-500">
                      {entityType === "employee" && entity && typeof entity === 'object'
                        ? (("jobTitle" in entity ? (entity as Employee).jobTitle || "" : "Employee"))
                        : entity && typeof entity === 'object' && "name" in entity
                           ? `${(entity as any).name}`
                           : "Team"
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-2">
              <Label htmlFor="license">Select License</Label>
              <Select value={selectedLicenseId} onValueChange={setSelectedLicenseId}>
                <SelectTrigger id="license">
                  <SelectValue placeholder="Select a license" />
                </SelectTrigger>
                <SelectContent>
                  {licenses.map((license) => {
                    // Calculate available seats (now includes both employee and team assignments)
                    const totalSeats = typeof license.totalSeats === 'number' && license.totalSeats >= 0 ? license.totalSeats : 0;
                    const assignedSeats = typeof license.assignedSeats === 'number' && license.assignedSeats >= 0 ? license.assignedSeats : 0;
                    const availableSeats = Math.max(0, totalSeats - assignedSeats);
                    const hasAvailableSeats = totalSeats > 0 && availableSeats > 0;
                    
                    return (
                      <SelectItem 
                        key={license.id} 
                        value={license.id.toString()}
                        disabled={totalSeats > 0 && !hasAvailableSeats}
                      >
                        <div className="flex flex-col">
                          <span>{getApplicationName(license.applicationId)} - {license.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {totalSeats === 0 
                              ? "Unlimited seats" 
                              : hasAvailableSeats 
                                ? `${availableSeats}/${totalSeats} seats available` 
                                : "No seats available"}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {selectedLicenseId && (
              <Card className="bg-slate-50/50">
                <CardContent className="pt-6">
                  {(() => {
                    const license = licenses.find(lic => lic.id === parseInt(selectedLicenseId, 10));
                    if (!license) return null;
                    
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Application:</span>
                          <span className="text-sm">{getApplicationName(license.applicationId)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">License:</span>
                          <span className="text-sm">{license.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Seats:</span>
                          <span className="text-sm">
                            <Badge variant="outline" className="ml-1">
                              {license.assignedSeats || 0} / {license.totalSeats}
                            </Badge>
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Renewal:</span>
                          <span className="text-sm">{formatDate(license.renewalDate)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isAssigning}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedLicenseId || isAssigning}>
            {isAssigning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Assign License
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}